import React from "react";
import { Guide } from "../../types/guide";
import { badgeStyle, cardStyle, emptyTextStyle, mutedTextStyle, sectionTitleStyle } from "../GuideDetailModal.styles";
import { formatDate } from "../GuideDetailModal.utils";

export function ReferencesTab({ guide }: { guide: Guide }) {
    const references = guide.onboarding?.references ?? [];

    if (!references.length) {
        return <div style={emptyTextStyle}>No references have been submitted yet.</div>;
    }

    return (
        <div style={{ display: "grid", gap: 12 }}>
            {references.map((ref) => (
                <div key={ref.id ?? `${ref.refType}-${ref.email}`} style={cardStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <div style={{ fontWeight: 600 }}>{ref.fullName ?? "Unnamed reference"}</div>
                        <span
                            style={{
                                ...badgeStyle,
                                background: ref.refType === "client" ? "#2C2731" : "#403851",
                                borderColor: ref.refType === "client" ? "#2C2731" : "#403851",
                                color: "#FEF6D6",
                            }}
                        >
                            {(ref.refType ?? "reference").replace(/_/g, " ")}
                        </span>
                    </div>
                    
                    <div style={{ color: "#2C2731", fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
                        <div>Email: {ref.email ?? "Not provided"}</div>
                        <div>Phone: {ref.phone ?? "Not provided"}</div>
                        {ref.company && <div>Company: {ref.company}</div>}
                        {ref.jobTitle && <div>Title: {ref.jobTitle}</div>}
                    </div>

                    {/* Client-specific fields */}
                    {ref.refType === "client" && (
                        <>
                            {ref.servicesProvided && (
                                <div style={{ marginTop: 8 }}>
                                    <div style={sectionTitleStyle}>Services Provided</div>
                                    <div style={{ color: "#403851", fontSize: 13 }}>{ref.servicesProvided}</div>
                                </div>
                            )}
                            {ref.relationshipDuration && (
                                <div style={{ marginTop: 8 }}>
                                    <div style={sectionTitleStyle}>Relationship & Duration</div>
                                    <div style={{ color: "#403851", fontSize: 13 }}>{ref.relationshipDuration}</div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Professional-specific fields */}
                    {ref.refType === "professional" && (
                        <>
                            {ref.professionalRelationship && (
                                <div style={{ marginTop: 8 }}>
                                    <div style={sectionTitleStyle}>Professional Relationship</div>
                                    <div style={{ color: "#403851", fontSize: 13 }}>{ref.professionalRelationship}</div>
                                </div>
                            )}
                            {ref.durationOfRelationship && (
                                <div style={{ marginTop: 8 }}>
                                    <div style={sectionTitleStyle}>Duration of Relationship</div>
                                    <div style={{ color: "#403851", fontSize: 13 }}>{ref.durationOfRelationship}</div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Admin/Review fields */}
                    {ref.reviewerNotes && (
                        <div style={{ marginTop: 8 }}>
                            <div style={sectionTitleStyle}>Reviewer Notes</div>
                            <div style={{ color: "#403851", fontSize: 13, lineHeight: 1.5 }}>{ref.reviewerNotes}</div>
                        </div>
                    )}

                    {/* Status timestamps */}
                    <div style={{ marginTop: 8, display: "flex", gap: 16, ...mutedTextStyle }}>
                        <span>Created: {formatDate(ref.createdAt)}</span>
                        {ref.contactedAt && <span>Contacted: {formatDate(ref.contactedAt)}</span>}
                        {ref.verifiedAt && <span>Verified: {formatDate(ref.verifiedAt)}</span>}
                    </div>
                </div>
            ))}
        </div>
    );
}
